/**
 * Chart — top-level multi-panel chart container.
 *
 * Manages the collection of `ChartPanel` instances and the shared drawing-tool
 * state that applies to whichever panel is currently active.
 *
 * Supported layout modes (controlled via Navbar):
 * - `"tab"` — single visible panel with a scrollable tab bar (default)
 * - `"2x1"`, `"1x2"`, `"2x2"`, `"3x1"`, `"1x3"`, `"2x3"`, `"3x2"`
 *   grid layouts rendered simultaneously
 *
 * Also hosts:
 * - `GlobalChartToolbar` — timeframe, chart type, drawing tools, indicators
 * - `MarketWatchPanel` — dockable symbol list with live LTP
 * - `TabBar` (local sub-component) — scrollable tab strip with arrow buttons
 */
import React, { useMemo, useState, useRef, useCallback, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { addPanel, removePanel, setActiveTab, setMarketWatchOpen } from "../../store/chart-slice";
import { getColorTheme } from "@service/local-storage-handler";
import { isMobile, isDesktop } from "@platform";
import ChartPanel, { ChartPanelHandle } from "./ChartPanel";
import GlobalChartToolbar from "./GlobalChartToolbar";
import ChartToolsSidebar from "./ChartToolsSidebar";
import MarketWatchPanel from "./MarketWatchPanel";
import { ChartSymbol, LayoutMode } from "../../types/chart.types";
import { DrawingTool } from "../../types/drawing.types";
import "./Chart.scss";

// ── Scrollable tab bar with left/right navigation arrows ─────────
const TabBar: React.FC<{
  panels: any[];
  activeTabId: string;
  onTabClick: (id: string) => void;
  onRemovePanel: (panelId: string) => void;
  colorTheme: string;
}> = ({ panels, activeTabId, onTabClick, onRemovePanel, colorTheme }) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  // Desktop-only right-click context menu for tabs
  const [tabContextMenu, setTabContextMenu] = useState<{ x: number; y: number; panelId: string } | null>(null);

  useEffect(() => {
    if (!tabContextMenu) return;
    const close = () => setTabContextMenu(null);
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") close(); };
    document.addEventListener("mousedown", close);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", close);
      document.removeEventListener("keydown", onKey);
    };
  }, [tabContextMenu]);

  const handleTabContextMenu = useCallback((e: React.MouseEvent, panelId: string) => {
    if (!isDesktop) return;
    e.preventDefault();
    e.stopPropagation();
    setTabContextMenu({ x: e.clientX, y: e.clientY, panelId });
  }, []);

  const updateScrollState = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 1);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 1);
  }, []);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    updateScrollState();
    el.addEventListener("scroll", updateScrollState, { passive: true });
    const ro = new ResizeObserver(updateScrollState);
    ro.observe(el);
    return () => {
      el.removeEventListener("scroll", updateScrollState);
      ro.disconnect();
    };
  }, [updateScrollState, panels.length]);

  // Auto-scroll the active tab into view when it changes (e.g. via Ctrl+Tab)
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const activeBtn = el.querySelector(".chart-tab.active") as HTMLElement | null;
    if (activeBtn) {
      activeBtn.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "nearest" });
    }
  }, [activeTabId]);

  const scroll = (dir: "left" | "right") => {
    const el = scrollRef.current;
    if (!el) return;
    const amount = el.clientWidth * 0.6;
    el.scrollBy({ left: dir === "left" ? -amount : amount, behavior: "smooth" });
  };

  const showArrows = canScrollLeft || canScrollRight;

  return (
    <div className={`chart-tab-bar-wrapper${isMobile ? " mobile-tab-bar" : ""}`}>
      {showArrows && (
        <div
          className={`tab-scroll-btn tab-scroll-left ${canScrollLeft ? "" : "disabled"}`}
          onClick={() => canScrollLeft && scroll("left")}
          role="button"
          title="Scroll tabs left"
        >
          ◀
        </div>
      )}
      <div className="chart-tab-bar" ref={scrollRef}>
        {panels.map((panel: any, index: number) => (
          <button
            key={panel.id}
            className={`chart-tab ${panel.id === activeTabId ? "active" : ""}`}
            onClick={() => onTabClick(panel.id)}
            onContextMenu={(e) => handleTabContextMenu(e, panel.id)}
            title={panel.symbol ? `${panel.symbol.symbol} (${panel.timeframe})` : "Home"}
          >
            <span className="tab-label">
              {panel.symbol ? panel.symbol.symbol : "Home"}
            </span>
            {panel.isLoading && <span className="tab-loading-dot" />}
          </button>
        ))}
      </div>
      {showArrows && (
        <div
          className={`tab-scroll-btn tab-scroll-right ${canScrollRight ? "" : "disabled"}`}
          onClick={() => canScrollRight && scroll("right")}
          role="button"
          title="Scroll tabs right"
        >
          ▶
        </div>
      )}

      {/* Desktop-only right-click context menu for tabs */}
      {tabContextMenu && (
        <div
          className={`tab-context-menu ${colorTheme}`}
          style={{
            left: Math.min(tabContextMenu.x, window.innerWidth - 120),
            bottom: window.innerHeight - tabContextMenu.y,
          }}
          onMouseDown={e => e.stopPropagation()}
        >
          <button
            className="tab-context-menu-item tab-context-menu-close"
            onClick={() => { setTabContextMenu(null); onRemovePanel(tabContextMenu.panelId); }}
          >
            Close
          </button>
        </div>
      )}
    </div>
  );
};

const Chart: React.FC = () => {
  const dispatch = useDispatch();
  const themeFromStore = useSelector(({ theme }) => theme.theme);
  const colorTheme = themeFromStore || getColorTheme() || "dark";
  const marketWatchOpen = useSelector((state: any) => state.chart?.marketWatchOpen || false);
  const toolsSidebarOpen = useSelector((state: any) => state.chart?.toolsSidebarOpen ?? false);
  
  const panels = useSelector((state: any) => state.chart?.panels || []);
  const watchlistSymbols = useSelector((state: any) => state.watchlist?.scrips || []);
  const selectedWatchlist = useSelector((state: any) => state.watchlist?.selectedWatchlist ?? null);
  const layoutMode: LayoutMode = useSelector((state: any) => state.chart?.layoutMode || "tab");
  const activeTabId: string | null = useSelector((state: any) => state.chart?.activeTabId || null);

  // ── Startup reconciliation: close panels whose symbols are no longer in the watchlist ──
  // Runs once, when the watchlist is first loaded after login. Panels whose symbol
  // was added in a previous session but never saved to the watchlist will be closed
  // so the chart doesn't show a stale/unsubscribed symbol.
  const hasReconciledRef = useRef(false);
  useEffect(() => {
    if (hasReconciledRef.current || !selectedWatchlist) return;
    hasReconciledRef.current = true;

    const watchlistSymbolSet = new Set(
      (watchlistSymbols as any[]).map((s) => s.symbol)
    );
    const panelsToClose = (panels as any[]).filter(
      (panel) => panel.symbol && !watchlistSymbolSet.has(panel.symbol.symbol)
    );
    panelsToClose.forEach((panel) => dispatch(removePanel(panel.id)));
  }, [selectedWatchlist, watchlistSymbols, panels, dispatch]);

  // ── Lifted drawing-tool state (global, shared across all panes) ──
  const [activeTool, setActiveTool] = useState<DrawingTool>("cursor");
  const [activeDrawingColor, setActiveDrawingColor] = useState("#2962FF");
  const [magnetEnabled, setMagnetEnabled] = useState(false);
  const [drawingsVisible, setDrawingsVisible] = useState(true);
  const [drawingCount, setDrawingCount] = useState(0);
  const [showActiveIndicators, setShowActiveIndicators] = useState(true);

  // ── Mobile toolbar toggle ─────────────────────────────────────
  const [mobileToolbarVisible, setMobileToolbarVisible] = useState(false);

  // ── Panel refs for imperative calls via GlobalChartToolbar ────────
  const panelRefsMap = useRef<Record<string, React.RefObject<ChartPanelHandle | null>>>({});
  // Ensure a ref exists for every panel
  panels.forEach((p: any) => {
    if (!panelRefsMap.current[p.id]) {
      panelRefsMap.current[p.id] = React.createRef<ChartPanelHandle>();
    }
  });
  // Clean up refs for removed panels
  Object.keys(panelRefsMap.current).forEach(id => {
    if (!panels.some((p: any) => p.id === id)) {
      delete panelRefsMap.current[id];
    }
  });

  // Memoize availableSymbols to prevent re-creating array on every render
  const availableSymbols: ChartSymbol[] = useMemo(() => {
    if (!Array.isArray(watchlistSymbols)) return [];
    return watchlistSymbols.map((scrip: any) => ({
      symbol: scrip.symbol,
      segment: scrip.segment,
      name: scrip.symbol,
      id: scrip.id,
    }));
  }, [watchlistSymbols]);

  const handleAddPanel = () => {
    dispatch(addPanel());
  };

  const handleTabClick = (panelId: string) => {
    dispatch(setActiveTab(panelId));
  };

  const getGridClass = () => {
    const count = panels.length;
    if (layoutMode === "tab") return "layout-tab";
    if (layoutMode === "horizontal") return "layout-horizontal";
    if (layoutMode === "vertical") return "layout-vertical";
    
    // Grid layout based on panel count
    if (count === 1) return "layout-1";
    if (count === 2) return "layout-2";
    if (count === 3) return "layout-3";
    if (count === 4) return "layout-4";
    if (count === 5) return "layout-5";
    if (count === 6) return "layout-6";
    if (count === 7) return "layout-7";
    if (count === 8) return "layout-8";
    if (count === 9) return "layout-9";
    return "layout-grid";
  };

  // Determine the effective active tab (fallback to first panel)
  const effectiveActiveTabId = activeTabId && panels.some((p: any) => p.id === activeTabId)
    ? activeTabId
    : panels[0]?.id || null;

  // Auto-create a panel when there are none (skip "No Charts Yet" screen)
  useEffect(() => {
    if (panels.length === 0) {
      dispatch(addPanel());
    }
  }, [panels.length, dispatch]);

  // Ref to the currently active pane (used by GlobalChartToolbar)
  const activePanelRef = effectiveActiveTabId
    ? panelRefsMap.current[effectiveActiveTabId] || { current: null }
    : { current: null };

  // Ctrl+Tab / Ctrl+Shift+Tab — cycle through chart tabs
  useEffect(() => {
    if (layoutMode !== "tab") return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!e.ctrlKey || e.key !== "Tab") return;
      if (panels.length < 2) return;
      e.preventDefault();
      const currentIndex = panels.findIndex((p: any) => p.id === effectiveActiveTabId);
      if (currentIndex === -1) return;
      const nextIndex = e.shiftKey
        ? (currentIndex - 1 + panels.length) % panels.length
        : (currentIndex + 1) % panels.length;
      dispatch(setActiveTab(panels[nextIndex].id));
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [panels, effectiveActiveTabId, layoutMode, dispatch]);

  // Poll drawing count from active panel
  useEffect(() => {
    const sync = () => {
      const h = activePanelRef.current;
      setDrawingCount(h ? h.getDrawingCount() : 0);
    };
    sync();
    const id = setInterval(sync, 500);
    return () => clearInterval(id);
  }, [activePanelRef, effectiveActiveTabId]);

  // ── Drawing toolbar callbacks ──────────────────────────────────
  const handleToolChange = useCallback((tool: DrawingTool) => setActiveTool(tool), []);
  const handleColorChange = useCallback((color: string) => setActiveDrawingColor(color), []);
  const handleMagnetToggle = useCallback(() => setMagnetEnabled(prev => !prev), []);
  const handleToggleDrawingsVisibility = useCallback(() => setDrawingsVisible(prev => !prev), []);

  const showMobileLayout = isMobile;

  return (
    <div className={`chart-page ${colorTheme}`}>
      {/* ── Desktop / web: toolbar as DIRECT flex child of chart-page ──────────
           This is the only reliable position on Android Chrome. Placing it
           inside chart-page-body > chart-content (4 levels deep with nested
           flex:1 + min-height:0) causes the flex engine to collapse it to 0px.
           At this level chart-page is simply: height:100dvh flex-col overflow:hidden.
           Toolbar gets its natural 36px; chart-page-body fills the rest via flex:1.
      ── */}
      {!showMobileLayout && panels.length > 0 && (
        <GlobalChartToolbar
          activePanelId={effectiveActiveTabId}
          activePanelRef={activePanelRef as React.RefObject<ChartPanelHandle | null>}
          colorTheme={colorTheme}
          activeTool={activeTool}
          onToolChange={handleToolChange}
          activeIndicatorsVisible={showActiveIndicators}
          onToggleActiveIndicators={() => setShowActiveIndicators((prev) => !prev)}
        />
      )}
      <div className="chart-page-body">
        {marketWatchOpen && (
          <MarketWatchPanel
            onClose={() => dispatch(setMarketWatchOpen(false))}
            colorTheme={colorTheme}
          />
        )}
        {/* ── Mobile/narrow-web: Fixed drawing tools sidebar (like market watch) ── */}
        {showMobileLayout && panels.length > 0 && toolsSidebarOpen && (
          <ChartToolsSidebar
            activeTool={activeTool}
            activeColor={activeDrawingColor}
            magnetEnabled={magnetEnabled}
            drawingsVisible={drawingsVisible}
            drawingCount={drawingCount}
            onToolChange={handleToolChange}
            onColorChange={handleColorChange}
            onZoomIn={() => { const h = activePanelRef.current; if (h) h.zoomIn(); }}
            onZoomOut={() => { const h = activePanelRef.current; if (h) h.zoomOut(); }}
            onMagnetToggle={handleMagnetToggle}
            onToggleVisibility={handleToggleDrawingsVisibility}
            onClearAll={() => { const h = activePanelRef.current; if (h) h.clearAllDrawings(); }}
            onUndo={() => { const h = activePanelRef.current; if (h) h.undoDrawing(); }}
            colorTheme={colorTheme}
          />
        )}
        <div className={`chart-content${showMobileLayout && layoutMode === "tab" && panels.length > 0 ? " mobile-tab-layout" : ""}`}>
          {/* ── Mobile: top bar — brand left, menu/hide button right ── */}
          {showMobileLayout && panels.length > 0 && (
            <div className="mobile-top-bar">
              {/* Brand mark — left side */}
              <div className="mobile-brand">
                <span className="mobile-brand-text">MarketView</span>
              </div>
              {/* Toolbar toggle — right side */}
              <button
                className="mobile-toolbar-toggle"
                onClick={() => setMobileToolbarVisible(v => !v)}
                title={mobileToolbarVisible ? "Hide Toolbar" : "Show Toolbar"}
              >
                <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
                  {mobileToolbarVisible ? (
                    <polyline points="6,15 12,9 18,15" />
                  ) : (
                    <><line x1="3" y1="6" x2="21" y2="6" /><line x1="3" y1="12" x2="21" y2="12" /><line x1="3" y1="18" x2="15" y2="18" /></>
                  )}
                </svg>
                <span className="mobile-toolbar-toggle-label">
                  {mobileToolbarVisible ? "Hide" : "Menu"}
                </span>
              </button>
            </div>
          )}
          {/* ── Mobile: toggle-gated toolbar (desktop toolbar is at chart-page level) ── */}
          {showMobileLayout && panels.length > 0 && mobileToolbarVisible && (
            <GlobalChartToolbar
              activePanelId={effectiveActiveTabId}
              activePanelRef={activePanelRef as React.RefObject<ChartPanelHandle | null>}
              colorTheme={colorTheme}
              activeTool={activeTool}
              onToolChange={handleToolChange}
              activeIndicatorsVisible={showActiveIndicators}
              onToggleActiveIndicators={() => setShowActiveIndicators((prev) => !prev)}
            />
          )}
          <div className="chart-body-row">
            {/* ── Drawing tools sidebar on the left (hidden on mobile and narrow-web) ── */}
            {!showMobileLayout && panels.length > 0 && toolsSidebarOpen && (
              <ChartToolsSidebar
                activeTool={activeTool}
                activeColor={activeDrawingColor}
                magnetEnabled={magnetEnabled}
                drawingsVisible={drawingsVisible}
                drawingCount={drawingCount}
                onToolChange={handleToolChange}
                onColorChange={handleColorChange}
                onZoomIn={() => {
                  const h = activePanelRef.current;
                  if (h) h.zoomIn();
                }}
                onZoomOut={() => {
                  const h = activePanelRef.current;
                  if (h) h.zoomOut();
                }}
                onMagnetToggle={handleMagnetToggle}
                onToggleVisibility={handleToggleDrawingsVisibility}
                onClearAll={() => {
                  const h = activePanelRef.current;
                  if (h) h.clearAllDrawings();
                }}
                onUndo={() => {
                  const h = activePanelRef.current;
                  if (h) h.undoDrawing();
                }}
                colorTheme={colorTheme}
              />
            )}

            {/* ── Mobile tools sidebar moved to fixed position at chart-page-body level ── */}
          <div className={`chart-grid ${getGridClass()}`}>
            {panels.map((panel: any) => (
              <div
                key={panel.id}
                className={`chart-panel-wrapper ${panel.id === effectiveActiveTabId ? "pane-active" : ""} ${layoutMode === "tab" && panel.id !== effectiveActiveTabId ? "tab-hidden" : ""} ${layoutMode === "tab" && panel.id === effectiveActiveTabId ? "tab-active" : ""}`}
                onMouseDown={() => dispatch(setActiveTab(panel.id))}
              >
                <ChartPanel
                  ref={panelRefsMap.current[panel.id]}
                  panelId={panel.id}
                  availableSymbols={availableSymbols}
                  activeTool={activeTool}
                  activeDrawingColor={activeDrawingColor}
                  magnetEnabled={magnetEnabled}
                  drawingsVisible={drawingsVisible}
                  showActiveIndicators={showActiveIndicators}
                  onToolChange={handleToolChange}
                  onColorChange={handleColorChange}
                  onMagnetToggle={handleMagnetToggle}
                  onToggleDrawingsVisibility={handleToggleDrawingsVisibility}
                  isActive={panel.id === effectiveActiveTabId}
                  isTabLayout={layoutMode === "tab"}
                />
              </div>
            ))}
          </div>
          </div>{/* end chart-body-row */}

          {/* Tab bar at the bottom in tab mode — DESKTOP only (direct child of chart-content) */}
          {!showMobileLayout && layoutMode === "tab" && panels.length > 0 && (
            <TabBar
              panels={panels}
              activeTabId={effectiveActiveTabId}
              onTabClick={handleTabClick}
              onRemovePanel={(panelId) => dispatch(removePanel(panelId))}
              colorTheme={colorTheme}
            />
          )}

          {panels.length === 0 && (
            <div className="chart-empty-state">
              <div className="empty-content">
                <div className="empty-icon">📈</div>
                <h2>No Charts Yet</h2>
                <p>Add your first chart panel to get started</p>
                <button className="add-first-panel-btn" onClick={handleAddPanel}>
                  Create Chart Panel
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
      {/* ── Mobile tab bar: DIRECT child of chart-page ──────────────────────
           Placing the tab bar here (not inside chart-content > chart-body-row)
           guarantees it ALWAYS gets its allocated height on Android WebView.
           Deep nesting + flex:1 + min-height:0 chains can cause the WebView
           flex engine to collapse the tab bar to 0px.
      ── */}
      {showMobileLayout && layoutMode === "tab" && panels.length > 0 && (
        <TabBar
          panels={panels}
          activeTabId={effectiveActiveTabId}
          onTabClick={handleTabClick}
          onRemovePanel={(panelId) => dispatch(removePanel(panelId))}
          colorTheme={colorTheme}
        />
      )}
    </div>
  );
};

export default Chart;
